import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { 
  ArrowLeft, 
  Plus, 
  Trash2,
  Edit2,
  Save,
  X,
  Loader2,
  ClipboardList,
  GripVertical,
  Volume2,
  Camera
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  "Verlichting",
  "Remmen",
  "Banden",
  "Ophanging",
  "Carrosserie",
  "Motor",
  "Uitlaat",
  "Veiligheid",
  "Interieur",
  "Overig"
];

const QUESTION_TYPES = [
  { value: "standard", label: "Standaard (Ja/Nee)", icon: null },
  { value: "decibel", label: "Decibel meting", icon: Volume2 },
  { value: "photo", label: "Foto upload", icon: Camera }
];

const AdminQuestionsPage = () => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [formData, setFormData] = useState({
    category: "",
    question: "",
    order: 0,
    question_type: "standard"
  });
  const [saving, setSaving] = useState(false);
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    try {
      const response = await axios.get(`${API}/questions/all`, {
        headers: getAuthHeaders()
      });
      setQuestions(response.data);
    } catch (error) {
      toast.error("Kon vragen niet laden");
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingQuestion(null);
    setFormData({
      category: CATEGORIES[0],
      question: "",
      order: questions.length + 1,
      question_type: "standard"
    });
    setShowDialog(true);
  };

  const handleEdit = (question) => {
    setEditingQuestion(question);
    setFormData({
      category: question.category,
      question: question.question,
      order: question.order,
      question_type: question.question_type || "standard"
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.question.trim()) {
      toast.error("Vul een vraag in");
      return;
    }

    setSaving(true);
    try {
      if (editingQuestion) {
        await axios.put(`${API}/questions/${editingQuestion.id}`, formData, {
          headers: getAuthHeaders()
        });
        toast.success("Vraag bijgewerkt");
      } else {
        await axios.post(`${API}/questions`, formData, {
          headers: getAuthHeaders()
        });
        toast.success("Vraag toegevoegd");
      }
      setShowDialog(false);
      fetchQuestions();
    } catch (error) {
      toast.error("Kon vraag niet opslaan");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (questionId) => {
    if (!window.confirm("Weet je zeker dat je deze vraag wilt verwijderen?")) {
      return;
    }

    try {
      await axios.delete(`${API}/questions/${questionId}`, {
        headers: getAuthHeaders()
      });
      toast.success("Vraag verwijderd");
      fetchQuestions();
    } catch (error) {
      toast.error("Kon vraag niet verwijderen");
    }
  };

  // Group questions by category
  const groupedQuestions = questions.reduce((acc, q) => {
    if (!acc[q.category]) {
      acc[q.category] = [];
    }
    acc[q.category].push(q);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-primary text-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => navigate("/")}
                className="text-white hover:bg-white/10"
                data-testid="back-button"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="font-heading font-black text-xl">Keuringsvragen</h1>
                <p className="text-white/70 text-sm">Beheer de keuringschecklist</p>
              </div>
            </div>
            <Button
              onClick={handleAdd}
              className="bg-safety-orange hover:bg-safety-orange-dark"
              data-testid="add-question-button"
            >
              <Plus className="w-5 h-5 mr-2" />
              Toevoegen
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : questions.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <ClipboardList className="w-12 h-12 text-muted-foreground/50 mx-auto mb-3" />
              <p className="text-muted-foreground">Nog geen keuringsvragen</p>
              <Button className="mt-4" onClick={handleAdd}>
                <Plus className="w-4 h-4 mr-2" />
                Eerste vraag toevoegen
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedQuestions).map(([category, categoryQuestions]) => (
              <Card key={category} data-testid={`category-section-${category}`}>
                <CardHeader className="pb-2">
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-primary"></span>
                    {category}
                    <span className="text-sm font-normal text-muted-foreground">
                      ({categoryQuestions.length})
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y">
                    {categoryQuestions
                      .sort((a, b) => a.order - b.order)
                      .map((question, index) => (
                        <div 
                          key={question.id} 
                          className={`flex items-center gap-4 p-4 ${!question.is_active ? 'opacity-50' : ''}`}
                          data-testid={`question-item-${index}`}
                        >
                          <GripVertical className="w-5 h-5 text-muted-foreground/50" />
                          <span className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-sm font-medium">
                            {question.order}
                          </span>
                          <div className="flex-1 flex items-center gap-2">
                            <p className="font-medium">{question.question}</p>
                            {question.question_type === "decibel" && (
                              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                                <Volume2 className="w-3 h-3" />dB
                              </span>
                            )}
                            {question.question_type === "photo" && (
                              <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                                <Camera className="w-3 h-3" />Foto
                              </span>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEdit(question)}
                              data-testid={`edit-question-${index}`}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-destructive hover:bg-destructive/10"
                              onClick={() => handleDelete(question.id)}
                              data-testid={`delete-question-${index}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent data-testid="question-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingQuestion ? "Vraag Bewerken" : "Nieuwe Vraag"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="category">Categorie</Label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
              >
                <SelectTrigger data-testid="category-select">
                  <SelectValue placeholder="Selecteer categorie" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="question">Vraag</Label>
              <Input
                id="question"
                value={formData.question}
                onChange={(e) => setFormData(prev => ({ ...prev, question: e.target.value }))}
                placeholder="Bijv. Remlichten werken correct"
                data-testid="question-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="question_type">Type vraag</Label>
              <Select
                value={formData.question_type}
                onValueChange={(value) => setFormData(prev => ({ ...prev, question_type: value }))}
              >
                <SelectTrigger data-testid="question-type-select">
                  <SelectValue placeholder="Selecteer type" />
                </SelectTrigger>
                <SelectContent>
                  {QUESTION_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      <div className="flex items-center gap-2">
                        {type.icon && <type.icon className="w-4 h-4" />}
                        {type.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {formData.question_type === "decibel" && "Invoerveld voor geluidsniveau in decibel"}
                {formData.question_type === "photo" && "Mogelijkheid om foto's te uploaden"}
                {formData.question_type === "standard" && "Standaard ja/nee checkbox"}
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="order">Volgorde</Label>
              <Input
                id="order"
                type="number"
                min="1"
                value={formData.order}
                onChange={(e) => setFormData(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                data-testid="order-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Annuleren
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="bg-primary"
              data-testid="save-question-button"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              <Save className="w-4 h-4 mr-2" />
              Opslaan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminQuestionsPage;
